import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  Layout,
  Breadcrumb,
  Table,
  Card,
  Row,
  Col,
  Tag,
  Input,
  Select,
  Button,
  Space,
  Statistic,
  Typography,
  Tooltip,
  Badge,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  ShopOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Sidebar from "../../layout/Sidebar";
import Navbar from "../../layout/Navbar";
import backend from "../../../api/api";
import { store } from "../../../store";

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;

const getConfig = () => {
  const token =
    localStorage.getItem("token") ||
    (store.getState().auth && store.getState().auth.token);
  return {
    headers: {
      Authorization: `Token ${token}`,
    },
  };
};

const InventoryMovementDashboard = () => {
  const [movements, setMovements] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // KPI data
  const [kpis, setKpis] = useState({
    total_movements: 0,
    total_stock_added: 0,
    total_stock_deducted: 0,
    net_stock_change: 0,
    counts_by_type: {},
  });

  // Filters
  const [keyword, setKeyword] = useState("");
  const [movementType, setMovementType] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");

  const fetchWarehouses = async () => {
    try {
      const res = await backend.get("api/product/warehouse/", getConfig());
      if (res.data && Array.isArray(res.data)) {
        setWarehouses(res.data);
      } else if (res.data && res.data.results) {
        setWarehouses(res.data.results);
      }
    } catch (err) {
      console.error("Failed to load warehouses", err);
    }
  };

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword) params.append("keyword", keyword);
      if (movementType) params.append("movement_type", movementType);
      if (selectedWarehouse) params.append("warehouse", selectedWarehouse);

      const res = await backend.get(
        `api/product/inventory-movements/?${params.toString()}`,
        getConfig()
      );

      if (res.data) {
        if (Array.isArray(res.data.results)) {
          setMovements(res.data.results);
        } else if (Array.isArray(res.data)) {
          setMovements(res.data);
        }
        
        setKpis({
          total_movements: res.data.total_movements || (res.data.results ? res.data.results.length : 0),
          total_stock_added: res.data.total_stock_added || 0,
          total_stock_deducted: res.data.total_stock_deducted || 0,
          net_stock_change: res.data.net_stock_change || 0,
          counts_by_type: res.data.counts_by_type || {},
        });
      }
    } catch (err) {
      console.error("Failed to load inventory movements", err);
    } finally {
      setLoading(false);
    }
  }, [keyword, movementType, selectedWarehouse]);

  useEffect(() => {
    fetchWarehouses();
    fetchMovements();
  }, [fetchMovements]);

  const handleResetFilters = () => {
    setKeyword("");
    setMovementType("");
    setSelectedWarehouse("");
  };

  const getMovementTag = (type, display) => {
    let color = "blue";
    if (type === "create") color = "green";
    if (type === "manual_update") color = "purple";
    if (type === "sales") color = "volcano";
    if (type === "transfer") color = "cyan";
    if (type === "remove") color = "red";
    return <Tag color={color} style={{ fontWeight: 600 }}>{display || type.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (val) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {val ? new Date(val).toLocaleString() : "N/A"}
        </Text>
      ),
    },
    {
      title: "Movement Type",
      dataIndex: "movement_type",
      key: "movement_type",
      width: 140,
      render: (type, record) => getMovementTag(type, record.movement_type_display),
    },
    {
      title: "Product & Variation",
      key: "product",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1890ff" }}>
            {record.product_title || "Unknown Product"}
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            <Tag size="small" style={{ marginTop: 2 }}>{record.variation_attribute || "Standard"}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Warehouse",
      dataIndex: "warehouse_name",
      key: "warehouse",
      width: 150,
      render: (name) => (
        <Space>
          <ShopOutlined style={{ color: "#595959" }} />
          <Text>{name || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Qty Delta",
      dataIndex: "quantity",
      key: "quantity",
      width: 110,
      align: "center",
      render: (qty) => {
        const isPositive = qty > 0;
        const color = isPositive ? "#3f8600" : qty < 0 ? "#cf1322" : "#595959";
        const icon = isPositive ? <ArrowUpOutlined /> : qty < 0 ? <ArrowDownOutlined /> : null;
        return (
          <span style={{ color, fontWeight: "bold", fontSize: 15 }}>
            {icon} {isPositive ? `+${qty}` : qty}
          </span>
        );
      },
    },
    {
      title: "Stock Progression",
      key: "progression",
      width: 160,
      align: "center",
      render: (_, record) => (
        <div style={{ background: "#f5f5f5", padding: "4px 8px", borderRadius: 4, display: "inline-block" }}>
          <Text strong style={{ color: "#595959" }}>{record.previous_quantity}</Text>
          <span style={{ margin: "0 8px", color: "#bfbfbf" }}>➔</span>
          <Text strong style={{ color: "#1890ff" }}>{record.new_quantity}</Text>
        </div>
      ),
    },
    {
      title: "Reference & Remarks",
      key: "reference",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.reference || "N/A"}</div>
          {record.remarks && (
            <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
              {record.remarks}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Performed By",
      dataIndex: "performed_by_name",
      key: "performed_by",
      width: 130,
      render: (user) => <Tag color="default">{user || "System"}</Tag>,
    },
  ];

  return (
    <Layout className="window-frame">
      <Sidebar />
      <Layout className="site-layout">
        <Navbar />
        <Content className="main-frame-content">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Products</Breadcrumb.Item>
            <Breadcrumb.Item>Inventory Movement Tracking</Breadcrumb.Item>
          </Breadcrumb>
          <div className="site-layout-background main-frame" style={{ padding: 24, minHeight: 600 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                {/* <Title level={3} style={{ margin: 0, color: "#1f1f1f" }}>
                  Inventory Movement Tracking
                </Title>
                <Text type="secondary">
                  Pure inventory stock progression log (Decoupled from invoice & financial accounting)
                </Text> */}
              </div>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchMovements}
                loading={loading}
              >
                Refresh Data
              </Button>
            </div>

            {/* KPI Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card bordered={false} style={{ background: "#f0f5ff", borderRadius: 8 }}>
                  <Statistic
                    title={<Text strong style={{ color: "#2f54eb" }}>Total Stock Movements</Text>}
                    value={kpis.total_movements}
                    prefix={<SwapOutlined />}
                    valueStyle={{ color: "#1d39c4", fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: "#f6ffed", borderRadius: 8 }}>
                  <Statistic
                    title={<Text strong style={{ color: "#389e0d" }}>Total Stock Added (+)</Text>}
                    value={kpis.total_stock_added}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: "#237804", fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: "#fff1f0", borderRadius: 8 }}>
                  <Statistic
                    title={<Text strong style={{ color: "#cf1322" }}>Total Stock Deducted (-)</Text>}
                    value={Math.abs(kpis.total_stock_deducted)}
                    prefix={<ArrowDownOutlined />}
                    valueStyle={{ color: "#a8071a", fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: "#f9f0ff", borderRadius: 8 }}>
                  <Statistic
                    title={<Text strong style={{ color: "#722ed1" }}>Net Stock Change</Text>}
                    value={kpis.net_stock_change}
                    prefix={<InfoCircleOutlined />}
                    valueStyle={{ color: "#531dab", fontWeight: 700 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Filters */}
            <Card style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <Input
                    placeholder="Search by product, reference, or remarks..."
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={fetchMovements}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    placeholder="Filter by Movement Type"
                    style={{ width: "100%" }}
                    value={movementType || undefined}
                    onChange={(val) => setMovementType(val || "")}
                    allowClear
                  >
                    <Option value="create">Initial Creation</Option>
                    <Option value="manual_update">Manual Adjustment</Option>
                    <Option value="sales">Sales Deduction</Option>
                    <Option value="transfer">Stock Transfer</Option>
                    <Option value="remove">Removal / Damage</Option>
                  </Select>
                </Col>
                <Col span={6}>
                  <Select
                    placeholder="Filter by Warehouse"
                    style={{ width: "100%" }}
                    value={selectedWarehouse || undefined}
                    onChange={(val) => setSelectedWarehouse(val || "")}
                    allowClear
                  >
                    {warehouses.map((w) => (
                      <Option key={w.id} value={w.id}>
                        {w.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4} style={{ textAlign: "right" }}>
                  <Button icon={<FilterOutlined />} onClick={handleResetFilters}>
                    Reset Filters
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={movements}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} entries` }}
              bordered
              size="middle"
              style={{ background: "#fff" }}
            />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default connect()(InventoryMovementDashboard);
